import axios from "axios"
import Crypto, {AES, RSA} from "../libs/Crypto"
import {type IKeyVal} from "../shared/Password"
import * as _ from "lodash"
import Auth, {type IUserPassword} from "./Auth"
import History from "./History"
import Utils from "../libs/Utils"
import JWTApi from "../libs/login/JWTApi"

function sessionKey(){
    const now = new Date()
    return Utils.formatDate("yyyyMMddHH", now) + ~~(now.getMinutes()/12)
}

export default class PassApi extends JWTApi {
    static _i:PassApi
    static get i(){
        if (!PassApi._i) PassApi._i = new PassApi()
        if (document.location.hostname=="localhost") (window as any).api = PassApi._i
        return PassApi._i
    }

    private _auth:Auth = new Auth()
    private _history:History = new History()

    get passwords(){ return this._auth.data }
    logout(){
        this._auth = new Auth()
        Utils.clipboard("")
        super.logout()
    }
    async loginOffline(email:string,pass:string) {
        this._history.setOffline()
        return this.initVault(null, pass)
    }
    async login(email:string,pass:string){
        return super.login(email, pass).then(_ => {
            this.sync(pass)
            return this
        })
    }
    async register(email: string, pass: string, name: string): Promise<this> {
        return super.register(email, pass, name).then(_ => {
            this.sync(pass)
            return this
        })
    }
    async sync(password:string){
        return this.api.post<IUserPassword>("/1/users/me/sync", this._history.history).then(this.onSync(password))
    }

    private onSync(password:string) {
        return async (data:{data:IUserPassword}) => this.initVault(data.data, password)
    }

    private async initVault(data:IUserPassword|null, password:string, remember?:boolean) {
        await this._auth.init(data, password)        
        if (data) this._history.flush()
        this.emit("auth", true)
        this.notify()

        if (remember) Crypto.generateKey().then(async (key) => {
            const {publicKey,privateKey} = key
            const rsa = await (new RSA()).init(publicKey, privateKey)
            const aes = await (new AES()).init(sessionKey())
            const value = await aes.encrypt(JSON.stringify({key:privateKey,pass:await rsa.encrypt(password)}))
            sessionStorage.setItem("sess", value)
        })
        return this
    }

    async resume(){
        try {
            const value = sessionStorage.getItem("sess")
            if (!value) throw {status:401}
    
            const aes = await (new AES()).init(sessionKey())
            const sess = JSON.parse(await aes.decrypt(value))
            const rsa = await (new RSA()).init("", sess.key)
            return this.initVault(null, await rsa.decrypt(sess.pass))
        }catch(e){
            sessionStorage.removeItem("sess")
            throw e
        }
    }

    // update password
    async updatePassword(old:string, pass:string){
        const data = await this._auth.updatePassword(old, pass)
        if (this._history.dirty) {
            for(const id in data) this._history.add(id, data[id])
        }
        else {
            try {
                const password = await this.encrypt(pass)
                await this.api.put<number>("/1/users/me", {password,data})
            }
            catch(e){
                for(const id in data) this._history.add(id, data[id])
            }
        }        
    }

    // create/update
    async create(name:string){
        const {id,value} = await this._auth.create(name)
        await this._update(id, value)
        this.notify()
    }

    async update(id:string, keyval:IKeyVal[]) {
        const value = await this._auth.update(id, keyval)
        await this._update(id, value)
        this.notify()
    }

    private async _update(id:string,value:string){
        if (this._history.dirty) this._history.add(id, value)
        else {
            try {
                await this.api.put<number>("/1/users/me/data/"+id, value, {headers:{"content-type":"application/json"}})
            }catch(e){
                this._history.add(id, value)
            }
        }
    }

    // delete
    async delete(id:string){
        if (this._history.dirty) this._history.add(id, null)
        else {
            try {await axios.delete<number>("/1/users/me/data/"+id, {headers:{"content-type":"application/json"}})}
            catch(e){
                this._history.add(id, null)
            }
        }
        this._auth.delete(id)
        this.notify()
    }

    notify(){
        this.emit("refresh", this.passwords)
    }
    clear(){
        this._history.clear()
        this._auth.clear()
    }
}
